import {
  INBOUND_DIRECTION,
  OUTBOUND_DIRECTION,
} from '../constants/dataTypeConstants';
import { priceBandConstants } from '../constants/priceBandConstants';
import gridEntryReader from '../data-parsing/gridEntryReader';
import quantileCalculator from '../data-parsing/quantileCalculator';

import type { QuotePrice } from './types';
import type {
  MonthDataGridElement,
  MonthGridData,
} from '../../selectors/types';

const coercePrice = (price: QuotePrice): number | null => {
  if (price === null || price === undefined) {
    return null;
  }
  return price;
};

const getPriceList = (priceGrid: MonthGridData): number[] => {
  if (priceGrid === null) return [];

  const allEntries: MonthDataGridElement[] = [];
  priceGrid.forEach((entry) => {
    if (Array.isArray(entry)) {
      allEntries.push(...entry);
    } else {
      allEntries.push(entry);
    }
  });

  const allPrices: number[] = [];

  allEntries.forEach((entry) => {
    const outboundPrice =
      coercePrice(
        gridEntryReader.getSpecificPrice(entry, OUTBOUND_DIRECTION, true),
      ) ??
      coercePrice(
        gridEntryReader.getSpecificPrice(entry, OUTBOUND_DIRECTION, false),
      );
    const inboundPrice =
      coercePrice(
        gridEntryReader.getSpecificPrice(entry, INBOUND_DIRECTION, true),
      ) ??
      coercePrice(
        gridEntryReader.getSpecificPrice(entry, INBOUND_DIRECTION, false),
      );

    if (outboundPrice !== null) {
      allPrices.push(outboundPrice);
    }
    if (inboundPrice !== null) {
      allPrices.push(inboundPrice);
    }
  });

  return allPrices;
};

const calculateColourBands = (priceGrid: MonthGridData): number[] => {
  if (!priceGrid) return [];
  const prices = getPriceList(priceGrid).sort((a, b) => a - b);
  const quantiles = quantileCalculator(prices, [
    ...priceBandConstants.PRICE_BAND_BOUNDARIES,
  ]);
  return quantiles.map((q) => q.value);
};

const getColourClass = (bands: number[], price: number | null): string => {
  if (!price) return priceBandConstants.DEFAULT_PRICE_CLASS;
  const priceBandsLength = priceBandConstants.NUMBER_OF_PRICE_BANDS - 1;
  if (!bands || bands.length !== priceBandsLength)
    return priceBandConstants.DEFAULT_PRICE_CLASS;

  let colourClass = priceBandConstants.PRICE_CLASSES[priceBandsLength];
  bands.some((band, index) => {
    if (price <= band) {
      colourClass = priceBandConstants.PRICE_CLASSES[index];
      return true;
    }
    return false;
  });

  return colourClass;
};

export default {
  calculateColourBands,
  getColourClass,
};
